import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Landmark, Banknote, ArrowDownToLine, Loader2, Check, X } from "lucide-react";
import { toast } from "sonner";

/** Real-money balance from track sales, separate from the virtual "Notes"
 * wallet handled by WalletPanel.jsx. Always a single USD balance per artist
 * — conversion to a local Mobile Money currency happens only at withdrawal
 * time (backend/payments/service.py), never before. */

// Mirrors payments/service.py's MIN_WITHDRAWAL_USD — kept here too so the
// artist sees the threshold before opening the modal (and the field itself
// enforces it), not just as a rejection after filling the whole form.
const MIN_WITHDRAWAL_USD = 50;

export default function PayoutPanel() {
  const [balances, setBalances] = useState(null); // null = loading
  const [payouts, setPayouts] = useState([]);
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [providers, setProviders] = useState(null); // null = loading, [] = none
  const [selectedKey, setSelectedKey] = useState(null); // `${id}::${payout_currency}`
  const [method, setMethod] = useState("");
  const [msisdn, setMsisdn] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [busy, setBusy] = useState(false);

  const offerKey = (p) => `${p.id}::${p.payout_currency}`;
  const selectedOffer = providers?.find((p) => offerKey(p) === selectedKey) || null;
  const knownOperators = selectedOffer?.operators || [];

  // Same idea as PurchaseModal.jsx: a provider with a confirmed operator
  // list (registry._PROVIDER_OPERATORS) gets a real selector instead of a
  // free-text field, defaulting to the first choice when the offer changes.
  useEffect(() => {
    if (knownOperators.length && !knownOperators.some((op) => op.code === method)) {
      setMethod(knownOperators[0].code);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey]);

  const load = async () => {
    const [{ data: bal }, { data: hist }] = await Promise.all([
      api.get("/payments/balance"),
      api.get("/payouts"),
    ]);
    setBalances(bal?.balances || []);
    setPayouts(hist || []);
  };

  useEffect(() => { load(); }, []);

  const openWithdraw = async () => {
    setAmount("");
    setSelectedKey(null);
    setMethod("");
    setMsisdn("");
    setFirstName("");
    setLastName("");
    setOpen(true);
    setProviders(null);
    try {
      // No currency filter — every (provider, local currency) pair with a
      // configured FX_RATE_USD_{currency} is a valid destination, the
      // artist picks directly (see registry.list_available_payout_providers).
      const { data } = await api.get("/payouts/providers");
      const list = data?.providers || [];
      setProviders(list);
      if (list.length) setSelectedKey(offerKey(list[0]));
    } catch {
      setProviders([]);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!amount || parseFloat(amount) < MIN_WITHDRAWAL_USD || !selectedOffer || !method || !msisdn || !firstName || !lastName) return;
    setBusy(true);
    try {
      await api.post("/payouts", {
        amount: parseFloat(amount),
        payout_currency: selectedOffer.payout_currency,
        provider: selectedOffer.id,
        destination: {
          method: method.trim(), msisdn: msisdn.trim(),
          first_name: firstName.trim(), last_name: lastName.trim(),
        },
      });
      toast.success("Retrait envoyé ✓");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  if (balances === null) return null;
  if (balances.length === 0) return null; // no real-money sale yet — nothing to show

  return (
    <div className="rounded-3xl p-5 border border-white/10 bg-gradient-to-br from-[#0A0A0A] via-zinc-950 to-[#0a1a18] mb-6" data-testid="payout-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F2FE] font-black flex items-center gap-2">
            <Landmark className="w-3.5 h-3.5" /> Gains ventes de musique
          </div>
          <div className="mt-2 space-y-1">
            {balances.map((b) => (
              <div key={b.currency} className="flex items-center gap-3 flex-wrap" data-testid={`balance-${b.currency}`}>
                <span className="text-2xl font-black font-display text-white tabular-nums">
                  {b.available.toFixed(2)} <span className="text-sm text-zinc-400 font-medium">{b.currency}</span>
                </span>
                <button
                  onClick={openWithdraw}
                  disabled={b.available < MIN_WITHDRAWAL_USD}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-[#00F2FE] to-[#8A2BE2] text-black font-black text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid={`open-payout-${b.currency}`}
                >
                  <ArrowDownToLine className="w-3.5 h-3.5" /> Retirer
                </button>
              </div>
            ))}
          </div>
          {balances.some((b) => b.available < MIN_WITHDRAWAL_USD) && (
            <p className="mt-2 text-[11px] text-zinc-500">
              Retrait possible à partir de {MIN_WITHDRAWAL_USD} $ (≈ 30 000 FCFA).
            </p>
          )}
        </div>
      </div>

      {payouts.length > 0 && (
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-2">
          {payouts.slice(0, 3).map((p) => (
            <div key={p.id} className="flex items-center gap-2 p-2.5 rounded-xl bg-white/5 border border-white/5">
              <div className={`w-7 h-7 rounded-full grid place-items-center ${p.status === "COMPLETED" ? "bg-emerald-500/20 text-emerald-400" : p.status === "FAILED" ? "bg-red-500/20 text-red-400" : "bg-amber-500/20 text-amber-400"}`}>
                {p.status === "COMPLETED" ? <Check className="w-3.5 h-3.5" /> : <Loader2 className="w-3.5 h-3.5 animate-spin" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-white font-bold tabular-nums">
                  {p.amount} {p.currency}
                  {p.payout_currency && p.payout_currency !== p.currency && (
                    <span className="text-zinc-500 font-normal"> → {p.payout_amount} {p.payout_currency}</span>
                  )}
                </div>
                <div className="text-[10px] text-zinc-500 truncate">{p.provider} · {p.status}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-xl grid place-items-center p-4" data-testid="payout-modal">
          <form onSubmit={submit} className="w-full max-w-md bg-zinc-950 border border-white/10 rounded-3xl p-5 anim-in relative">
            <button type="button" onClick={() => setOpen(false)} className="absolute top-4 right-4 p-1.5 rounded-full bg-white/5 hover:bg-white/10 text-zinc-400" aria-label="close">
              <X className="w-4 h-4" />
            </button>
            <h3 className="text-xl font-black font-display text-white flex items-center gap-2">
              <Banknote className="w-5 h-5 text-[#00F2FE]" /> Retrait
            </h3>

            <label className="block mt-4">
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Montant (USD)</span>
              <input
                type="number" step="0.01" value={amount} onChange={(e) => setAmount(e.target.value)}
                min={MIN_WITHDRAWAL_USD}
                className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40"
                data-testid="payout-amount-input"
              />
              <span className="text-[11px] text-zinc-500 mt-1 block">Minimum {MIN_WITHDRAWAL_USD} $ (≈ 30 000 FCFA) par retrait.</span>
            </label>

            <div className="mt-3">
              <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Devise de destination</span>
              {providers === null && (
                <p className="text-xs text-zinc-500 mt-1 flex items-center gap-1.5"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement…</p>
              )}
              {providers !== null && providers.length === 0 && (
                <p className="text-xs text-amber-300 mt-1" data-testid="no-payout-methods">
                  Aucun moyen de retrait disponible en ce moment.
                </p>
              )}
              {providers !== null && providers.length > 0 && (
                <>
                  <select
                    value={selectedKey || ""} onChange={(e) => setSelectedKey(e.target.value)}
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none"
                    data-testid="payout-provider-select"
                  >
                    {providers.map((p) => (
                      <option key={offerKey(p)} value={offerKey(p)}>{p.label} — {p.payout_currency}</option>
                    ))}
                  </select>
                  {selectedOffer && amount > 0 && (
                    <p className="text-xs text-[#00F2FE]/90 mt-1">
                      ≈ {(parseFloat(amount) * selectedOffer.rate).toLocaleString(undefined, { maximumFractionDigits: 0 })} {selectedOffer.payout_currency}
                    </p>
                  )}
                </>
              )}
            </div>

            {providers && providers.length > 0 && (
              <>
                <div className="block mt-3">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Opérateur Mobile Money</span>
                  {knownOperators.length > 0 ? (
                    <div className="mt-1 grid grid-cols-2 gap-2">
                      {knownOperators.map((op) => (
                        <button
                          key={op.code}
                          type="button"
                          onClick={() => setMethod(op.code)}
                          data-testid={`payout-operator-${op.code}`}
                          className={`px-3 py-2.5 rounded-xl border text-sm font-semibold transition ${
                            method === op.code
                              ? "border-[#00F2FE] bg-[#00F2FE]/10 text-white"
                              : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
                          }`}
                        >
                          {op.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <input
                      value={method} onChange={(e) => setMethod(e.target.value)}
                      placeholder="ex. mtn_bj, orange_ci"
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40"
                      data-testid="payout-operator-input"
                    />
                  )}
                </div>
                <label className="block mt-3">
                  <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Numéro Mobile Money</span>
                  <input
                    value={msisdn} onChange={(e) => setMsisdn(e.target.value)}
                    placeholder="+237600000000"
                    className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40"
                    data-testid="payout-phone-input"
                  />
                </label>
                <div className="grid grid-cols-2 gap-2 mt-3">
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Prénom</span>
                    <input
                      value={firstName} onChange={(e) => setFirstName(e.target.value)}
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40"
                      data-testid="payout-first-name-input"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wider text-zinc-400 font-bold">Nom</span>
                    <input
                      value={lastName} onChange={(e) => setLastName(e.target.value)}
                      className="mt-1 w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white outline-none focus:border-[#00F2FE]/40"
                      data-testid="payout-last-name-input"
                    />
                  </label>
                </div>
              </>
            )}

            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setOpen(false)} className="flex-1 py-3 rounded-full bg-white/5 text-zinc-300 text-sm font-bold" data-testid="payout-cancel">Annuler</button>
              <button
                type="submit" disabled={busy || !providers?.length || !amount || parseFloat(amount) < MIN_WITHDRAWAL_USD}
                className="flex-1 py-3 rounded-full bg-gradient-to-r from-[#00F2FE] to-[#8A2BE2] text-black text-sm font-black disabled:opacity-50"
                data-testid="payout-submit"
              >
                {busy ? "…" : "Confirmer"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
